/**
 * Loader cache, stale-while-revalidate.
 *
 * Every screen re-fetched on every visit, including a visit to a screen whose
 * data had not changed since it was last read. The event stream already says,
 * per table, when something *has* changed - so the fetch is only needed when it
 * says so, and even then it need not be waited for.
 *
 * What a screen does on open:
 *
 *   nothing cached  -> fetch, and wait. There is nothing else to show.
 *   cached, current -> serve it. No request at all.
 *   cached, stale   -> serve it anyway, fetch behind it, redraw when that
 *                      lands. The old answer is at most one round trip out of
 *                      date, and it is on screen in the frame the person
 *                      arrives rather than after a skeleton.
 *
 * Three rules make this safe rather than merely fast:
 *
 * 1. An entry may only be cached if every table behind it announces its
 *    changes. A table with no change trigger can never invalidate its entry, so
 *    that entry would be stale forever - with no round trip coming to end it.
 *    `cache-tables.ts` is the list of tables that do announce, and
 *    `test/cache-invalidation.test.ts` fails if it ever names one the database
 *    has no trigger for.
 *
 * 2. The key carries the scope. Clinic and store are part of it, so a cached
 *    read can never be served under a scope it was not read under - and the
 *    cache is cleared outright on a scope switch and on sign-out anyway.
 *
 * 3. If live updates stop, caching stops. The stream is the only thing that
 *    knows an entry has gone stale; without it, "stale" would have no bound and
 *    this would just be a way to show people old data with no way out. Serving
 *    one extra request per screen is the correct behaviour when the
 *    invalidation channel is down.
 */
import { get, session } from './api.ts';
import { NOTIFYING } from './cache-tables.ts';

interface Entry {
  tables: readonly string[];
  data: unknown;
  /** A change was announced for one of `tables`; the copy here predates it. */
  stale: boolean;
}

const entries = new Map<string, Entry>();

/** Refreshes already running, so a burst of events queues one request per key. */
const refreshing = new Map<string, Promise<void>>();

/** Off until the stream says it is live; see rule 3. */
let live = false;

/**
 * Ask the router to re-run its loaders.
 *
 * Registered by `connectRealtime`, which is where the router actually is. A
 * background refresh has no other way to get what it fetched onto the screen:
 * the loader it belongs to returned the stale copy some time ago and nothing
 * will read the cache again until something asks it to.
 */
let revalidate: () => void = () => undefined;

export function setRevalidate(fn: () => void): void {
  revalidate = fn;
}

/** The event stream reports whether invalidation is actually being delivered. */
export function setCacheLive(value: boolean): void {
  live = value;
  if (!value) clearCache();
}

/** Drop everything. Scope switch, sign-in, sign-out, and stream resync. */
export function clearCache(): void {
  entries.clear();
  refreshing.clear();
}

/**
 * Mark the entries fed by one table as out of date.
 *
 * Marked, not deleted. Deleting means the next screen that needs it waits on
 * the network with nothing to show, which is the flash of skeleton this cache
 * exists to remove. The previous answer is still the best thing available until
 * the new one arrives, and it is at most one round trip out of date.
 *
 * Table granularity, not row: an entry is a list or a summary far more often
 * than it is one row, so a row-level map would have to track which ids each
 * response contained - much more bookkeeping to avoid a refetch that is one
 * request.
 */
export function invalidateTable(table: string): boolean {
  let hit = false;
  for (const entry of entries.values()) {
    if (entry.tables.includes(table)) {
      entry.stale = true;
      hit = true;
    }
  }
  return hit;
}

/**
 * Fetch a stale entry again, behind whatever is already on screen.
 *
 * On failure the entry is dropped rather than left marked: showing the last
 * good answer indefinitely because the refresh keeps failing is exactly the
 * "silently old data" this is meant to avoid. Dropping it means the next read
 * waits for the network and surfaces the error like any other load.
 */
function refresh(key: string, path: string, tables: readonly string[]): void {
  if (refreshing.has(key)) return;

  const run = get<unknown>(path)
    .then((fresh) => {
      // The scope may have changed while this was in the air, and clearCache
      // would have removed the entry. Do not resurrect it.
      const current = entries.get(key);
      if (!current || !live) return;

      const changed = JSON.stringify(fresh) !== JSON.stringify(current.data);
      entries.set(key, { tables, data: fresh, stale: false });
      // Only disturb the screen when the answer actually differs. Most refreshes
      // after a change event are for rows the user was not looking at.
      if (changed) revalidate();
    })
    .catch(() => {
      entries.delete(key);
    })
    .finally(() => {
      refreshing.delete(key);
    });

  refreshing.set(key, run);
}

/**
 * GET a path, serving the cached copy while one exists.
 *
 * A current copy is returned as it is. A stale copy is returned too - straight
 * away, with a refresh started behind it, and the screen updates when that
 * lands. Only a screen with nothing cached at all waits on the network.
 *
 * `tables` is what the response is built from: name every table the endpoint
 * reads, because a table left out is a change that will not invalidate this.
 */
export async function cachedGet<T>(path: string, tables: readonly string[]): Promise<T> {
  const unwatched = tables.filter((t) => !NOTIFYING.has(t));
  if (unwatched.length > 0) {
    // A programming error, not a runtime condition: fetch, and say so loudly in
    // development rather than silently serving something that cannot expire.
    console.error(`cachedGet(${path}): no change stream for ${unwatched.join(', ')}`);
    return get<T>(path);
  }

  const { activeClinicId, activeStoreId } = await session();
  const key = `${activeClinicId ?? '-'}/${activeStoreId ?? '-'}/${path}`;

  if (live) {
    const hit = entries.get(key);
    if (hit) {
      if (hit.stale) refresh(key, path, tables);
      return hit.data as T;
    }
  }

  const data = await get<T>(path);
  if (live) entries.set(key, { tables, data, stale: false });
  return data;
}
